export const desaturateColor = (color: string) => {
	// If color is already using rgba or has opacity, return it
	if (color.startsWith('rgba')) return color;
	// If it's a hex color, convert it to have opacity
	if (color.startsWith('#')) {
		return `${color}40`; // Adding 50% opacity (80 in hex)
	}
	// If it's an rgb color or other format, assume it's rgb and add opacity
	return color.replace('rgb', 'rgba').replace(')', ', 0.7)');
};

/**
 * Creates a light background version of a color suitable for selected items
 * @param color - Hex color code (with #)
 * @returns Background color with low opacity for better contrast
 */
export const createLightBackgroundColor = (color: string) => {
	// If color is already using rgba or has opacity, return it
	if (color.startsWith('rgba')) return color;
	// If it's a hex color, convert it to have very light opacity
	if (color.startsWith('#')) {
		return `${color}30`; // Adding 10% opacity
	}
	// If it's an rgb color or other format, assume it's rgb and add opacity
	return color.replace('rgb', 'rgba').replace(')', ', 0.3)');
};

export const luminanceThreshold = 0.412;

/**
 * Creates a border color version with medium opacity
 * @param color - Hex color code (with #)
 * @returns Border color with medium opacity
 */
export const createBorderColor = (color: string) => {
	// If color is already using rgba or has opacity, return it
	if (color.startsWith('rgba')) return color;
	// If it's a hex color, convert it to have medium opacity
	if (color.startsWith('#')) {
		return `${color}30`; // Adding 30% opacity
	}
	// If it's an rgb color or other format, assume it's rgb and add opacity
	return color.replace('rgb', 'rgba').replace(')', ', 0.3)');
};

/**
 * Calculates appropriate text color (black or white) based on background color contrast
 * @param backgroundColor - Hex color code (with or without #)
 * @param secondary - Optional parameter to get a muted version of the text color for descriptions
 * @returns '#ffffff' for white or '#000000' for black
 */
export const getTextColorForBackground = (
	backgroundColor: string,
	secondary?: boolean
): string => {
	// Remove # if it exists
	const hex = backgroundColor.replace('#', '');

	// Convert to RGB
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	// Calculate luminance - using perceived brightness formula
	// https://www.w3.org/TR/WCAG20-TECHS/G18.html
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Return white for dark backgrounds, black for light backgrounds
	// Threshold of 0.55 tends to work well for readability
	const baseColor = luminance > luminanceThreshold ? '#000000' : '#ffffff';

	// If secondary is true, return a more muted version of the text color
	if (secondary) {
		return luminance > luminanceThreshold ? '#374151' : '#D1D5DB';
	}

	return baseColor;
};

/**
 * Calculates appropriate text color (black or white) based on background color contrast
 * @param backgroundColor - Hex color code (with or without #)
 * @returns '#ffffff' for white or '#000000' for black
 */
export const getThemeFromBackground = (backgroundColor: string): string => {
	// Remove # if it exists
	const hex = backgroundColor.replace('#', '');

	// Convert to RGB
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	// Calculate luminance - using perceived brightness formula
	// https://www.w3.org/TR/WCAG20-TECHS/G18.html
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Return white for dark backgrounds, black for light backgrounds
	// Threshold of 0.55 tends to work well for readability
	return luminance > luminanceThreshold ? 'light' : 'dark';
};

/**
 * Calculates appropriate text color (black or white) based on background color contrast
 * @param backgroundColor - Hex color code (with or without #)
 * @returns '#ffffff' for white or '#000000' for black
 */
export const getInputColorFromBackground = (
	backgroundColor: string
): string => {
	// Remove # if it exists
	const hex = backgroundColor.replace('#', '');

	// Convert to RGB
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	// Calculate luminance - using perceived brightness formula
	// https://www.w3.org/TR/WCAG20-TECHS/G18.html
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Return white for dark backgrounds, black for light backgrounds
	// Threshold of 0.55 tends to work well for readability
	return luminance > luminanceThreshold ? '#ffffff' : '#27272a';
};

// Color mappings for the 8 standard colors
export const buttonColors = {
	pink: {
		light: 'from-pink-400',
		dark: 'to-pink-500',
		base: 'border-pink-500',
		text: 'text-pink-600',
	},
	purple: {
		light: 'from-purple-400',
		dark: 'to-purple-500',
		base: 'border-purple-500',
		text: 'text-purple-600',
	},
	sky: {
		light: 'from-sky-400',
		dark: 'to-sky-500',
		base: 'border-sky-500',
		text: 'text-sky-600',
	},
	emerald: {
		light: 'from-emerald-400',
		dark: 'to-emerald-500',
		base: 'border-emerald-500',
		text: 'text-emerald-600',
	},
	yellow: {
		light: 'from-yellow-400',
		dark: 'to-yellow-500',
		base: 'border-yellow-500',
		text: 'text-yellow-600',
	},
	orange: {
		light: 'from-orange-400',
		dark: 'to-orange-500',
		base: 'border-orange-500',
		text: 'text-orange-600',
	},
	green: {
		light: 'from-green-400',
		dark: 'to-green-500',
		base: 'border-green-500',
		text: 'text-green-600',
	},
	blue: {
		light: 'from-blue-400',
		dark: 'to-blue-500',
		base: 'border-blue-500',
		text: 'text-blue-600',
	},
	slate: {
		light: 'from-slate-400',
		dark: 'to-slate-500',
		base: 'border-slate-500',
		text: 'text-slate-600',
	},
	grey: {
		light: 'from-gray-400',
		dark: 'to-gray-500',
		base: 'border-gray-500',
		text: 'text-gray-600',
	},
} as const;

// Map from hex colors to our standardized colors
export const colorMap: Record<string, keyof typeof buttonColors> = {
	'#FFBFE9': 'pink', // pink-300
	'#BE185D': 'pink', // pink-700
	'#D8B4FE': 'purple', // purple-300
	'#7E22CE': 'purple', // purple-700
	'#783bff': 'purple', // purple-700
	'#4d6feb': 'purple', // purple-700
	'#a780ff': 'purple', // purple-700
	'#7DD3FC': 'sky', // sky-300
	'#0369A1': 'sky', // sky-700
	'#6EE7B7': 'emerald', // emerald-300
	'#047857': 'emerald', // emerald-700
	'#FEF08A': 'yellow', // yellow-200
	'#475569': 'slate', // slate-600
	'#FB923C': 'orange', // orange-400
	'#EA580C': 'orange', // orange-600
	'#86EFAC': 'green', // green-300
	'#15803D': 'green', // green-700
	'#93C5FD': 'blue', // blue-300
	'#1D4ED8': 'blue', // blue-700
	'#52525b': 'grey', // grey-600
};

// Added utility functions for color manipulation

// Convert a hex string to its RGB components
export const hexToRgb = (hex: string) => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return { r, g, b };
};

// Generate a darker (shaded) version of a color
export const getShadedColor = (hex: string, shade: number) => {
	const { r, g, b } = hexToRgb(hex);
	return `rgb(${Math.max(0, r - shade)}, ${Math.max(0, g - shade)}, ${Math.max(0, b - shade)})`;
};

// Generate a lighter version of a color
export const getLightenedColor = (hex: string, lighten: number) => {
	const { r, g, b } = hexToRgb(hex);
	return `rgb(${Math.min(255, r + lighten)}, ${Math.min(255, g + lighten)}, ${Math.min(255, b + lighten)})`;
};
